import { classifyChipValue } from './BatchChipGroup.visual'

// Pure chip-value logic extracted from search-quesionnaries.component.tsx so the
// "what value does a toggled/edited chip get?" rules (Compendium B8/B9) are unit-testable
// without rendering. Same pattern as chip-resolve / buildBatchSavePayload.
//
// B8: a JSON root default_answer='Unknown' is a PLACEHOLDER, not a real saved answer — so a
//     fresh toggle still sets Yes/5 and the inline editor still fires the propagated preview
//     (the fba829ed regression: 'Unknown' was treated as saved → toggle set 'Unknown',
//     preset-intent never resolved to confirm, no preview).
// B9: a REAL saved value (scale=3, 'No') is preserved on reopen — never overwritten by the
//     toggle default.

// The single discriminator both rules share: is this a real, provider-entered saved answer?
// null / '' are empty; 'Unknown' (radio) and '3' (scale midpoint) are the NEUTRAL PLACEHOLDER —
// the value a chip sits at BEFORE a real answer — not a provider-entered answer. classifyChipValue
// maps both to 'unknown'; this stays in lockstep so the toggle/edit path treats them as unanswered
// (QUICK_ENTRY_CHIP_STATE_MACHINE_ADR D3 — the scale-'3' fix: a click confirms them to Yes/5).
export function isRealSavedAnswer(value: any): boolean {
  if (value == null || value === '') return false
  const v = String(value)
  return v !== 'Unknown' && v !== '3'
}

// Shared value primitives (identical across all chip surfaces S1–S4).
// confirm default = the value a NEW chip gets when selected; deny = when denied.
export function chipConfirmDefault(item: any): string {
  return (item?.first_component_type || 'scale') === 'scale' ? '5' : 'Yes'
}
export function chipDenyValue(item: any): string {
  return (item?.first_component_type || 'scale') === 'scale' ? '1' : 'No'
}

// DEPRECATED (QUICK_ENTRY_CHIP_STATE_MACHINE_ADR D2/D3): the "preserve the saved value on toggle"
// rule (B9) is RETIRED — a single click on an ANSWERED chip now opens the editor (it never re-toggles
// a value), and neutral '3'/'Unknown' confirm to Yes/5. The click path routes through
// resolveChipGesture below; this primitive is kept only for the editor-preview helpers. With the
// corrected isRealSavedAnswer, chipToggleValue(scale,'3') now returns '5' (not '3').
export function chipToggleValue(item: any, originalVal: any): string {
  if (isRealSavedAnswer(originalVal)) return String(originalVal)
  return chipConfirmDefault(item)
}

// QUICK_ENTRY_CHIP_STATE_MACHINE_ADR §3.3 — the SINGLE decider for what a chip gesture does,
// shared by BOTH surfaces (S1 Search + S2 Category) so their behavior can never drift again.
// Routing keys on the chip's VALUE class only (classifyChipValue — the same classifier that colors
// the chip, so routing and color can't disagree):
//   • unanswered / neutral placeholder ('3' / 'Unknown') → CONFIRM to Yes/5 (D3)
//   • answered (Yes OR No) → OPEN the inline editor — never a silent flip (D2; protects a pertinent No)
//   • the ✓-icon deselect gesture → clear to unanswered / null (D5)
// "Double-click = open editor" is realized by the click cycle: a fresh chip's 1st click confirms
// (→ answered), the 2nd opens the editor; an already-answered chip opens on the 1st click.
export type ChipGesture = 'click' | 'iconDeselect'
export interface ChipGestureAction {
  action: 'confirm' | 'edit' | 'deselect'
  value: string | null   // value to write for confirm/deselect; ignored for 'edit'
}
export function resolveChipGesture(firstResponse: any, item: any, gesture: ChipGesture): ChipGestureAction {
  if (gesture === 'iconDeselect') return { action: 'deselect', value: null }
  const cls = classifyChipValue(firstResponse) // 'yes' | 'no' | 'unknown' | 'unanswered'
  if (cls === 'yes' || cls === 'no') return { action: 'edit', value: null }
  return { action: 'confirm', value: chipConfirmDefault(item) }
}

// The first_response the inline editor opens with.
// - real saved answer → open showing it (B9), so presetIntent reflects the saved value, not 'confirm'
// - otherwise → the pending toggle value if any, else the item's own fallback (B8: 'Unknown'
//   is not real, so a freshly-toggled 'Unknown'-default chip opens with the pending 'Yes')
export function chipEditFirstResponse(originalVal: any, pendingVal: any, fallback: any): any {
  if (isRealSavedAnswer(originalVal)) return originalVal
  return pendingVal != null ? pendingVal : fallback
}

// B27 seam fix — a batch chip's DISPLAYED value must reflect the unsaved-edit overlay
// (`pendingAnswers`), which the toggle / deny-all handlers write for EVERY chip keyed by name,
// regardless of whether the chip lives in `item`, `recommendedDiagnosis`, or the eval-union
// `evalCatalog`. The Search build site used to source first_response ONLY from the data row
// (`resultsByName`/`allItemsByName` → item/recommendedDiagnosis). A catalog-only chip (F118 R3
// eval-union surfacing) is in NEITHER of those, so its toggle-write — which setItem /
// setRecommendedDiagnosis `.map` never matches — was silently dropped: first_response stayed
// null → the chip never re-colored after B25 moved fill onto the first_response prop.
// Read the pending overlay FIRST (a toggle/deny value, always a non-null string), then the saved
// backend value, then unanswered. On toggle-OFF the handler DELETES the pending entry, so this
// correctly falls back to the saved value. Pure so the seam is unit-pinned without rendering.
export function resolveBatchItemDisplayResponse(
  name: string,
  pendingAnswers: Map<string, any> | undefined,
  savedResponse: any,
): any {
  const pending = pendingAnswers?.get(name)
  if (pending && pending.value != null) return pending.value
  return savedResponse ?? null
}

// B26 live-refresh core: after save, a branched (inline-edited) chip must re-color to its new
// answer without a reload. Its real answer is the ROOT of the edited answer set
// (answerSet[chip.first_question_id]) — NOT the chip's stale toggle `.value` (still 'Yes' from
// the initial click). Returns { chipKey: rootValue } for each edited chip whose root resolved.
// Pure so it can be unit-pinned; search passes resolveChip by name, category by id.
export function computeBranchedRefresh(
  editedAnswers: Map<string, any> | Array<[string, any]>,
  resolveChip: (key: string) => any | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  const entries = editedAnswers instanceof Map ? Array.from(editedAnswers.entries()) : editedAnswers
  for (const [key, answerSet] of entries) {
    const chip = resolveChip(key)
    const rootQid = chip?.first_question_id
    const rootVal = rootQid ? (answerSet as any)?.[rootQid]?.value : undefined
    if (rootVal != null) out[key] = String(rootVal)
  }
  return out
}
